import { ActionType } from '../infrastructure/actionTypes';
import {
  IBranding,
  ICoverImage,
  ICrop,
  IDocument,
  ILogo,
  IValidationItem,
  ProductCategory,
  IBlank,
  ICropDivider,
  ITechSheet,
  ISeedCategory,
  ISeedDivider,
  IFillerImage,
  IShelfDivider,
  ITechReferenceSheet,
  IPostcardImage,
  IPosterImage,
  ITagline,
} from '../infrastructure/interfaces';

export interface IWizardState {
  productType: IDocument | null;
  brandings: IBranding[];
  coverImages: ICoverImage[];
  posterImages: IPosterImage[];
  postcardImages: IPostcardImage[];
  crops: ICrop[];
  fillerImages: IFillerImage[];
  logos: ILogo[];
  productCategories: ProductCategory[];
  pagesCount: number;
  validation: {
    pagesCountInput: IValidationItem,
  };
  disclaimers: any[];
  agronomyReferenceSheetsPublished: ITechReferenceSheet[];
  dividers: IShelfDivider[];
  blanksPublished: IBlank[];
  techSheetsPublished: ITechSheet[];
  cropDividers: ICropDivider[];
  seedCategories: ISeedCategory[];
  seedDividers: ISeedDivider[];
  taglines: ITagline[];
}

const wizzardInitialState: IWizardState = {
  productType: null,
  brandings: [],
  coverImages: [],
  posterImages: [],
  postcardImages: [],
  crops: [],
  fillerImages: [],
  logos: [],
  productCategories: [],
  pagesCount: 4,
  disclaimers: [],
  agronomyReferenceSheetsPublished: [],
  dividers: [],
  blanksPublished: [],
  techSheetsPublished: [],
  cropDividers: [],
  seedCategories: [],
  seedDividers: [],
  taglines: [],
  validation: {
    pagesCountInput: {
      cssSelector: 'validate-pages-count',
      status: true,
      message: '',
    },
  },
};
const updateCrops = (state: IWizardState, action: any) => {
  let result;
  const { crops } = action.document;
  if (crops && state.crops.length) {
    const newCrops = state.crops.map((crop) => {
      const simpleCrop = crops.filter((c: any) => {
        return c.id === crop.id;
      });
      return simpleCrop[0] || crop;
    });
    result = {
      ...state,
      crops: newCrops,
    };
  } else if (crops) {
    result = {
      ...state,
      crops,
    };
  } else {
    result = state;
  }
  return result;
};

export default (state = wizzardInitialState, action: any): IWizardState => {
  switch (action.type) {
    case ActionType.GET_CROPS:
      const { crops } = action;
      return {
        ...state,
        crops,
      };
    case ActionType.CREATE_CROP_DONE:
      const { newCrop }  = action;
      const createdCrop = state.crops.concat(newCrop);
      return { ...state, crops: createdCrop}
    case ActionType.DELETE_CROP_DONE:
      const filteredCrops = state.crops.filter(c => c.id !== action.cropID);
      return { ...state, crops: filteredCrops };
    case ActionType.OPEN_DOCUMENT_DONE:
      {
        return updateCrops(state, action);
      }
    case ActionType.SAVE_DOCUMENT_DONE:
      {
        return updateCrops(state, action);
      }
    case ActionType.GET_BRANDINGS:
      const { brandings } = action;
      return { ...state, brandings };
    case ActionType.DELETE_BRANDING_DONE:
      {
        const updatedBrandings = state.brandings.filter(b => b.id !== action.id);
        return { ...state, brandings: updatedBrandings };
      }
    case ActionType.UPDATE_BRANDING_DONE:
      {
        const { branding } = action;
        const updatedBrandings = state.brandings.map(b => b.id === branding.id ? branding : b);
        return { ...state, brandings: updatedBrandings };
      }
    case ActionType.CREATE_BRANDING_DONE:
      {
        const { branding } = action;
        const updatedBrandings = state.brandings.concat(branding);
        return { ...state, brandings: updatedBrandings };
      }
    case ActionType.DELETE_LOGO_DONE:
      {
        const updatedLogos = state.logos.filter(l => l.id !== action.id);
        return { ...state, logos: updatedLogos };
      }
    case ActionType.UPDATE_LOGO_DONE:
      {
        const { logo } = action;
        const updatedLogos = state.logos.map(l => l.id === logo.id ? logo : l);
        return { ...state, logos: updatedLogos };
      }
    case ActionType.CREATE_LOGO_DONE:
      {
        const { logo } = action;
        const updatedLogos = state.logos.concat(logo);
        return { ...state, logos: updatedLogos };
      }
    case ActionType.ARCHIVE_LOGO_DONE:
      {
        const { logo } = action;
        const updatedLogos = state.logos.filter(l => l.id !== logo.id);
        return { ...state, logos: updatedLogos };
      }
    case ActionType.DELETE_DISCLAIMER_DONE:
      {
        const updatedDisclaimers = state.disclaimers.filter(d => d.id !== action.id);
        return { ...state, disclaimers: updatedDisclaimers };
      }
    case ActionType.UPDATE_DISCLAIMER_DONE:
      {
        const { disclaimer } = action;
        const updatedDisclaimers = state.disclaimers.map(d => d.id === disclaimer.id ? disclaimer : d);
        return { ...state, disclaimers: updatedDisclaimers };
      }
    case ActionType.CREATE_DISCLAIMER_DONE:
      {
        const { disclaimer } = action;
        const updatedDisclaimers = state.disclaimers.concat(disclaimer);
        return { ...state, disclaimers: updatedDisclaimers };
        }

    case ActionType.DELETE_TAGLINE_DONE:
      {
        const updatedTaglines = state.taglines.filter(t => t.id !== action.id);
        return { ...state, taglines: updatedTaglines };
      }
    case ActionType.UPDATE_TAGLINE_DONE:
      {
        const { tagline } = action;
        const updatedTaglines = state.taglines.map(t => t.id === tagline.id ? tagline : t);
        return { ...state, taglines: updatedTaglines };
      }
    case ActionType.CREATE_TAGLINE_DONE:
      {
        const { tagline } = action;
        const updatedTaglines = state.taglines.concat(tagline);
        return { ...state, taglines: updatedTaglines };
      }

    case ActionType.GET_COVER_IMAGES_DONE:
      const { coverImages } = action;
      return { ...state, coverImages };
    case ActionType.CREATE_COVER_IMAGE_DONE:
      const { newCoverImage } = action;
      const updatedCoverImages = state.coverImages.concat(newCoverImage);
      return { ...state, coverImages: updatedCoverImages };
    case ActionType.DELETE_COVER_IMAGE_DONE:
      {
        const updatedCoverImages = state.coverImages.filter(c => c.id !== action.id);
        return { ...state, coverImages: updatedCoverImages };
      }
    case ActionType.GET_POSTER_IMAGES_DONE:
      const { posterImages } = action;
      return { ...state, posterImages };
    case ActionType.CREATE_POSTER_IMAGE_DONE:
      const { newPosterImage } = action;
      const updatedPosterImages = state.posterImages.concat(newPosterImage);
      return { ...state, posterImages: updatedPosterImages };
      case ActionType.ARCHIVE_POSTER_IMAGE_DONE:
        {
          const { posterImage } = action;
          const updatedPosterImages = state.posterImages.filter(p => p.id !== posterImage.id);
          return { ...state, posterImages: updatedPosterImages };
        }
    case ActionType.GET_POSTCARD_IMAGE_DONE:
      const { postcardImages } = action;
      return { ...state, postcardImages };
    case ActionType.CREATE_POSTCARD_IMAGE_DONE:
      const { newPostcardImage } = action;
      const updatedPostcardImages = state.postcardImages.concat(newPostcardImage);
      return { ...state, postcardImages: updatedPostcardImages };
    case ActionType.DELETE_POSTCARD_IMAGE_DONE:
    {
      const updatedPostcardImages = state.postcardImages.filter(p => p.id !== action.id);
      return { ...state, postcardImages: updatedPostcardImages };
    }
    case ActionType.ARCHIVE_POSTCARD_IMAGE_DONE:
    {
      const { postcardImage } = action;
      const updatedPostcardImages = state.postcardImages.filter(p => p.id !== postcardImage.id);
      return { ...state, postcardImages: updatedPostcardImages };
    }
    case ActionType.GET_DISCLAIMERS:
      const { disclaimers } = action;
      return { ...state, disclaimers };
    case ActionType.GET_LOGOS:
      const { logos } = action;
      return { ...state, logos };
    case ActionType.GET_PRODUCT_CATEGORIES_DONE:
      const { productCategories } = action;
      return { ...state, productCategories };
    case ActionType.GET_SHELF_DIVIDERS_DONE:
      const { dividers } = action;
      return { ...state, dividers };

    case ActionType.GET_PUBLISHED_AGRONOMY_REFERENCE_SHEETS_DONE:
      const { agronomyReferenceSheetsPublished } = action;
      return { ...state, agronomyReferenceSheetsPublished };
    case ActionType.EDIT_AGRONOMY_REFERENCE_SHEET_DONE:
        const { updatedAgronomyReferenceSheet } = action;
        const sheetsAfterToggle = updatedAgronomyReferenceSheet.published
          ? state.agronomyReferenceSheetsPublished.concat(updatedAgronomyReferenceSheet)
          : state.agronomyReferenceSheetsPublished.filter((a: ITechReferenceSheet) => a.id != updatedAgronomyReferenceSheet.id)
      return { ...state, agronomyReferenceSheetsPublished: sheetsAfterToggle };

    case ActionType.GET_PUBLISHED_BLANKS_DONE:
      const { blanksPublished } = action;
      return { ...state, blanksPublished };
    case ActionType.TOGGLE_BLANK_PUBLISH_DONE:
      const { toggledBlank } = action
      const blanksAfterToggle = toggledBlank.published
      ? state.blanksPublished.concat(toggledBlank)
      : state.blanksPublished.filter((b: IBlank) => b.id != toggledBlank.id)
      return { ...state, blanksPublished: blanksAfterToggle };

    case ActionType.GET_PUBLISHED_TECH_SHEETS_DONE:
      const { techSheetsPublished } = action;
      return { ...state, techSheetsPublished };
    case ActionType.TOGGLE_TECH_SHEET_PUBLISH_DONE:
      const { toggledTechSheet } = action
      const techSheetsAfterToggle = toggledTechSheet.published
      ? state.techSheetsPublished.concat(toggledTechSheet)
      : state.techSheetsPublished.filter((t: ITechSheet) => t.id != toggledTechSheet.id)
      return { ...state, techSheetsPublished: techSheetsAfterToggle };
    case ActionType.GET_CROP_DIVIDERS_DONE:
      const { cropDividers } = action;
      return { ...state, cropDividers };
    case ActionType.GET_SEED_CATEGORIES_DONE:
        const { seedCategories } = action;
        return { ...state, seedCategories };
    case ActionType.GET_SEED_DIVIDERS_DONE:
      const { seedDividers } = action;
      return { ...state, seedDividers };
    case ActionType.GET_TAGLINES:
      const { taglines } = action;
      return { ...state, taglines };
    case ActionType.UPDATE_SEED_DIVIDER_DONE:
      const { updatedSeedDivider } = action;
      const seedDividersUpdated = state.seedDividers.map(s => {
        if (s.id !== updatedSeedDivider.id) {
          return s;
        }
        return updatedSeedDivider;
      });
      return { ...state, seedDividers: seedDividersUpdated };
    case ActionType.UPDATE_SHELF_DIVIDERS_DONE:
      const { updatedShelfDivider } = action;
      const shelfDividersUpdated = state.dividers.map( s => {
        if (s.id !== updatedShelfDivider.id) {
          return s;
        }
        return updatedShelfDivider;
      });
      return { ...state, dividers: shelfDividersUpdated };
    case ActionType.CHANGE_CROP_IMAGE:
      const newCrops = state.crops.map((item) => {
        if (item.id === action.crop.id) {
          item.defaultImage = action.image;
        }
        return item;
      });
      return {
        ...state,
        crops: newCrops,
      };
    case ActionType.SETUP_WIZARD: {
      return {
        ...state,
        productType: action.prodType,
        pagesCount: wizzardInitialState.pagesCount,
      };
    }
    case ActionType.VALIDATE_PAGES_COUNT_VALUE:
      let newValue = +action.value.currentTarget.value;
      const stateValidation = state.validation;
      let _validation = {
        cssSelector: 'validate-pages-count',
        status: true,
        message: '',
      };
      if (newValue < 16) {
        const remainder = newValue % 4;
        if (remainder !== 0) {
          _validation = {
            cssSelector: 'validate-pages-count',
            status: false,
            message: 'Please enter another quantity',
          };
        }
      } else if (newValue >= 16) {
        const remainder = newValue % 2;
        if (remainder !== 0) {
          _validation = {
            cssSelector: 'validate-pages-count',
            status: false,
            message: 'Please enter another quantity',
          };
        }
      }
      if (newValue < 4) {
        action.value.currentTarget.value = 4;
        newValue = 4;
        _validation = {
          cssSelector: '',
          status: true,
          message: '',
        };
      }
      return {
        ...state,
        pagesCount: newValue,
        validation: {
          ...stateValidation,
          pagesCountInput: _validation,
        },
      };
    case ActionType.RESET_VALIDATIONS: {
      return {
        ...state,
        validation: {
          pagesCountInput: wizzardInitialState.validation.pagesCountInput,
        },
      };
    }

    case ActionType.GET_FILLER_IMAGE_DONE:
      const { fillerImages } = action;
      return { ...state, fillerImages };

    case ActionType.EDIT_FILLER_IMAGE_DONE:
      const { updatedFillerImage } = action;
      const fillerImagesEdited = state.fillerImages.map((f) => {
        return (f.id == updatedFillerImage.id) ? updatedFillerImage : f
      });
      return { ...state, fillerImages: fillerImagesEdited };

    case ActionType.DELETE_FILLER_IMAGE_DONE:
      const fillerImagesDeleted = state.fillerImages.filter(image => image.id !== action.fillerImageId);
      return { ...state, fillerImages: fillerImagesDeleted }

    case ActionType.CREATE_FILLER_IMAGE_DONE:
      const { newFillerImage } = action;
      const fillerImagesAppended = state.fillerImages.concat(newFillerImage);
      return { ...state, fillerImages: fillerImagesAppended };

    default:
      return state;
  }
};



// WEBPACK FOOTER //
// ./src/reducers/wizard.ts